import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService, showToast } from './api.service';

@Component({
  selector: 'app-admin-customer-feedback',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="p-4 md:p-5 min-h-full bg-[#F7FAFC] overflow-y-auto">
      <div class="max-w-[1500px] mx-auto">
        <div class="flex flex-col lg:flex-row lg:items-end justify-between gap-3 mb-5">
          <div>
            <p class="text-[9px] font-bold uppercase tracking-[0.16em] text-[#1A6FA6] mb-1">Customer Experience</p>
            <h1 class="text-xl font-semibold text-slate-900 tracking-tight">Customer Feedback</h1>
            <p class="text-slate-500 text-xs">Review ratings, service recovery, and testimonial permissions from completed work.</p>
          </div>
          <button type="button" (click)="loadFeedback()" class="h-9 px-3 bg-white border border-slate-200 text-slate-700 text-xs font-semibold rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2">
            <i class="ti ti-refresh text-sm"></i> Refresh
          </button>
        </div>

        <div class="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
          <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3">
            <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Total</p>
            <p class="text-xl font-semibold text-slate-900 mt-1">{{ feedback().length }}</p>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3">
            <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Submitted</p>
            <p class="text-xl font-semibold text-[#1A6FA6] mt-1">{{ submittedCount() }}</p>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3">
            <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Follow-up</p>
            <p class="text-xl font-semibold text-rose-600 mt-1">{{ followUpCount() }}</p>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3">
            <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Testimonials</p>
            <p class="text-xl font-semibold text-emerald-600 mt-1">{{ testimonialCount() }}</p>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3">
            <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Average Rating</p>
            <p class="text-xl font-semibold text-slate-900 mt-1">{{ averageRating() }}</p>
          </div>
        </div>

        <div class="bg-white rounded-xl shadow-sm overflow-hidden border border-[#E7EEF5]">
          <div class="px-4 py-3 flex items-center gap-3 flex-wrap border-b border-slate-100">
            <div class="relative flex-1 min-w-full sm:min-w-[220px] max-w-sm">
              <i class="ti ti-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[15px]"></i>
              <input type="text" placeholder="Search feedback..." [ngModel]="searchQuery()" (ngModelChange)="searchQuery.set($event)"
                     class="w-full h-9 pl-9 pr-3 rounded-lg text-xs border border-slate-200 bg-slate-50 focus:outline-none focus:border-[#2B9AD2] placeholder:text-slate-400">
            </div>
            <select [ngModel]="statusFilter()" (ngModelChange)="statusFilter.set($event)" class="h-9 px-3 rounded-lg text-xs border border-slate-200 bg-slate-50 text-slate-600 focus:outline-none cursor-pointer">
              <option value="">All Statuses</option>
              <option value="Requested">Requested</option>
              <option value="Submitted">Submitted</option>
              <option value="Needs Follow-up">Needs Follow-up</option>
              <option value="Resolved">Resolved</option>
              <option value="Approved Testimonial">Approved Testimonial</option>
            </select>
            <select [ngModel]="ratingFilter()" (ngModelChange)="ratingFilter.set($event)" class="h-9 px-3 rounded-lg text-xs border border-slate-200 bg-slate-50 text-slate-600 focus:outline-none cursor-pointer">
              <option value="">All Ratings</option>
              <option value="low">1-2 stars</option>
              <option value="neutral">3 stars</option>
              <option value="high">4-5 stars</option>
            </select>
          </div>

          <div class="md:hidden p-3 space-y-3 bg-slate-50/70">
            @if (isLoading()) {
              <div class="bg-white rounded-xl border border-slate-100 px-4 py-8 text-center text-sm text-slate-400">Loading feedback...</div>
            } @else {
              @for (item of filteredFeedback(); track item.id) {
                <div class="bg-white rounded-xl border border-[#E7EEF5] p-3 shadow-sm">
                  <div class="flex items-start gap-3">
                    <div class="w-10 h-10 rounded-lg flex items-center justify-center font-black text-sm shrink-0" [ngClass]="ratingClass(item)">
                      {{ item.rating || '-' }}
                    </div>
                    <div class="min-w-0 flex-1">
                      <div class="flex items-start justify-between gap-2">
                        <div class="min-w-0">
                          <p class="font-semibold text-slate-900 text-sm truncate">{{ item.customer_name || 'Customer' }}</p>
                          <p class="text-[10px] text-slate-400 mt-0.5">{{ item.model_type }} {{ item.reference_id }}</p>
                        </div>
                        <span class="px-2 py-0.5 rounded-md text-[9px] font-bold border uppercase tracking-wide shrink-0" [ngClass]="statusClass(item.status)">
                          {{ item.status || 'Requested' }}
                        </span>
                      </div>
                      <p class="text-xs text-slate-600 mt-2">{{ item.comments || 'No comment recorded.' }}</p>
                      <div class="flex flex-wrap gap-2 mt-3">
                        <button type="button" (click)="openStatusUpdate(item, 'Needs Follow-up')" class="action-btn border-rose-200 text-rose-700 hover:bg-rose-50">Follow-up</button>
                        <button type="button" (click)="openStatusUpdate(item, 'Resolved')" class="action-btn border-emerald-200 text-emerald-700 hover:bg-emerald-50">Resolved</button>
                      </div>
                    </div>
                  </div>
                </div>
              } @empty {
                <div class="bg-white rounded-xl border border-slate-100 px-4 py-10 text-center text-sm text-slate-400">No feedback found.</div>
              }
            }
          </div>

          <div class="hidden md:block overflow-x-auto">
            <table class="w-full text-left text-sm">
              <thead>
                <tr class="bg-slate-50">
                  <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Customer</th>
                  <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Reference</th>
                  <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider text-center">Rating</th>
                  <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Comment</th>
                  <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider text-center">Status</th>
                  <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                @if (isLoading()) {
                  <tr><td colspan="6" class="px-4 py-8 text-center text-sm text-slate-400">Loading feedback...</td></tr>
                } @else {
                  @for (item of filteredFeedback(); track item.id) {
                    <tr class="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                      <td class="px-4 py-3">
                        <p class="font-semibold text-slate-900 text-sm">{{ item.customer_name || 'Customer' }}</p>
                        <p class="text-[10px] text-slate-400">{{ item.customer_email || item.customer_phone || 'No contact' }}</p>
                      </td>
                      <td class="px-4 py-3">
                        <p class="text-xs font-semibold text-slate-700">{{ item.model_type || 'Reference' }}</p>
                        <p class="text-[10px] text-slate-400">{{ item.reference_id || '-' }}</p>
                      </td>
                      <td class="px-4 py-3 text-center">
                        <span class="inline-flex w-9 h-9 rounded-lg items-center justify-center font-black text-sm" [ngClass]="ratingClass(item)">
                          {{ item.rating || '-' }}
                        </span>
                      </td>
                      <td class="px-4 py-3 max-w-[420px]">
                        <p class="text-xs text-slate-700 line-clamp-2">{{ item.comments || 'No comment recorded.' }}</p>
                        <p class="text-[10px] text-slate-400 mt-1">{{ item.submitted_at ? (item.submitted_at | date:'d MMM y, h:mm a') : (item.requested_at | date:'d MMM y') }}</p>
                      </td>
                      <td class="px-4 py-3 text-center">
                        <span class="px-2.5 py-1 rounded-md text-[10px] font-bold border uppercase tracking-wide" [ngClass]="statusClass(item.status)">
                          {{ item.status || 'Requested' }}
                        </span>
                        @if (item.testimonial_permission == 1) {
                          <p class="text-[10px] font-bold uppercase tracking-wide text-emerald-600 mt-1">Testimonial OK</p>
                        }
                      </td>
                      <td class="px-4 py-3">
                        <div class="flex items-center justify-end gap-2">
                          <button type="button" (click)="openStatusUpdate(item, 'Needs Follow-up')" class="action-btn border-rose-200 text-rose-700 hover:bg-rose-50">Follow-up</button>
                          <button type="button" (click)="openStatusUpdate(item, 'Resolved')" class="action-btn border-emerald-200 text-emerald-700 hover:bg-emerald-50">Resolved</button>
                          @if (item.testimonial_permission == 1) {
                            <button type="button" (click)="openStatusUpdate(item, 'Approved Testimonial')" class="action-btn border-sky-200 text-sky-700 hover:bg-sky-50">Testimonial</button>
                          }
                        </div>
                      </td>
                    </tr>
                  } @empty {
                    <tr><td colspan="6" class="px-4 py-10 text-center text-sm text-slate-400">No feedback found.</td></tr>
                  }
                }
              </tbody>
            </table>
          </div>
        </div>

        @if (statusModalItem()) {
          <div class="fixed inset-0 z-50 bg-slate-950/40 backdrop-blur-[1px] flex items-center justify-center p-4">
            <div class="w-full max-w-lg bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
              <div class="px-4 py-3 border-b border-slate-100 flex items-start justify-between gap-3">
                <div>
                  <p class="text-[10px] font-bold uppercase tracking-[0.16em] text-[#1A6FA6]">Feedback status</p>
                  <h2 class="text-base font-bold text-slate-900 mt-1">{{ statusModalStatus() }}</h2>
                  <p class="text-xs text-slate-500 mt-1">{{ statusModalItem()?.customer_name || 'Customer' }} · {{ statusModalItem()?.model_type }} {{ statusModalItem()?.reference_id }}</p>
                </div>
                <button type="button" (click)="closeStatusModal()" class="w-8 h-8 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50">
                  <i class="ti ti-x"></i>
                </button>
              </div>
              <div class="p-4">
                <label class="block">
                  <span class="block text-[11px] font-semibold text-slate-600 mb-1">Follow-up notes</span>
                  <textarea [(ngModel)]="statusModalNotes" rows="5" class="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none focus:border-[#1A6FA6]" placeholder="Record what the office did or needs to do next."></textarea>
                </label>
                <div class="mt-4 flex items-center justify-end gap-2">
                  <button type="button" (click)="closeStatusModal()" class="h-9 px-3 rounded-lg border border-slate-200 bg-white text-xs font-bold text-slate-600 hover:bg-slate-50">Cancel</button>
                  <button type="button" (click)="submitStatusUpdate()" [disabled]="isSavingStatus()" class="h-9 px-4 rounded-lg bg-[#0C3A5A] text-white text-xs font-bold hover:bg-[#082d46] disabled:opacity-60">
                    {{ isSavingStatus() ? 'Saving...' : 'Save Status' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    .action-btn {
      height: 2rem;
      padding: 0 .75rem;
      border-width: 1px;
      border-radius: .5rem;
      background: #fff;
      font-size: 11px;
      font-weight: 700;
      transition: background .15s ease;
      white-space: nowrap;
    }
  `]
})
export class AdminCustomerFeedbackComponent implements OnInit {
  private api = inject(ApiService);

  feedback = signal<any[]>([]);
  isLoading = signal(false);
  searchQuery = signal('');
  statusFilter = signal('');
  ratingFilter = signal('');
  statusModalItem = signal<any | null>(null);
  statusModalStatus = signal('');
  isSavingStatus = signal(false);
  statusModalNotes = '';

  submittedCount = computed(() => this.feedback().filter(item => item.status === 'Submitted').length);
  followUpCount = computed(() => this.feedback().filter(item => this.needsFollowUp(item)).length);
  testimonialCount = computed(() => this.feedback().filter(item => Number(item.testimonial_permission) === 1 && item.status !== 'Approved Testimonial').length);
  averageRating = computed(() => {
    const ratings = this.feedback()
      .map(item => Number(item.rating || 0))
      .filter(rating => Number.isFinite(rating) && rating > 0);
    if (!ratings.length) return 'N/A';
    const average = ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length;
    return `${average.toFixed(1)} / 5`;
  });

  filteredFeedback = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const status = this.statusFilter();
    const ratingFilter = this.ratingFilter();
    return this.feedback().filter(item => {
      const rating = Number(item.rating || 0);
      const queryMatches = !query || [
        item.customer_name,
        item.customer_email,
        item.customer_phone,
        item.reference_id,
        item.model_type,
        item.comments,
        item.service_category
      ].some(value => String(value || '').toLowerCase().includes(query));
      const statusMatches = !status || item.status === status;
      const ratingMatches = !ratingFilter
        || (ratingFilter === 'low' && rating > 0 && rating <= 2)
        || (ratingFilter === 'neutral' && rating === 3)
        || (ratingFilter === 'high' && rating >= 4);
      return queryMatches && statusMatches && ratingMatches;
    });
  });

  ngOnInit() {
    this.loadFeedback();
  }

  loadFeedback() {
    this.isLoading.set(true);
    this.api.getCustomerFeedback().subscribe({
      next: (res: any) => {
        this.feedback.set(res.success ? (res.data?.items || []) : []);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        showToast('error', 'Failed to load customer feedback.');
      }
    });
  }

  openStatusUpdate(item: any, status: string) {
    this.statusModalItem.set(item);
    this.statusModalStatus.set(status);
    this.statusModalNotes = item.follow_up_notes || '';
  }

  closeStatusModal() {
    if (this.isSavingStatus()) return;
    this.statusModalItem.set(null);
    this.statusModalStatus.set('');
    this.statusModalNotes = '';
  }

  submitStatusUpdate() {
    const item = this.statusModalItem();
    const status = this.statusModalStatus();
    if (!item || !status) return;

    this.isSavingStatus.set(true);
    this.api.updateCustomerFeedbackStatus({ id: Number(item.id), status, follow_up_notes: this.statusModalNotes }).subscribe({
      next: (res: any) => {
        this.isSavingStatus.set(false);
        if (!res.success) {
          showToast('error', res.message || 'Failed to update feedback.');
          return;
        }
        showToast('success', 'Feedback updated.');
        const notes = this.statusModalNotes.trim();
        const resolvedAt = status === 'Resolved' || status === 'Approved Testimonial' ? new Date().toISOString() : null;
        this.feedback.update(items => items.map(current => current.id === item.id ? { ...current, status, follow_up_notes: notes || null, resolved_at: resolvedAt } : current));
        this.closeStatusModal();
      },
      error: () => {
        this.isSavingStatus.set(false);
        showToast('error', 'Failed to update feedback.');
      }
    });
  }

  ratingClass(item: any): string {
    const rating = Number(item.rating || 0);
    if (rating > 0 && rating <= 2) return 'bg-rose-50 text-rose-700';
    if (rating === 3) return 'bg-amber-50 text-amber-700';
    if (rating >= 4) return 'bg-emerald-50 text-emerald-700';
    return 'bg-slate-50 text-slate-500';
  }

  statusClass(status: string): string {
    if (status === 'Needs Follow-up') return 'bg-rose-50 text-rose-700 border-rose-100';
    if (status === 'Submitted') return 'bg-sky-50 text-[#1A6FA6] border-sky-100';
    if (status === 'Resolved' || status === 'Approved Testimonial') return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    return 'bg-amber-50 text-amber-700 border-amber-100';
  }

  private needsFollowUp(item: any): boolean {
    if (this.isFeedbackClosed(item.status)) return false;
    const rating = Number(item.rating || 0);
    return item.status === 'Needs Follow-up' || (rating > 0 && rating <= 3);
  }

  private isFeedbackClosed(status: string): boolean {
    return status === 'Resolved' || status === 'Approved Testimonial';
  }
}
